/**
 * App.tsx
 * Router principal de la aplicación PTEL Tool
 *
 * @version 0.7.5
 * @description Layout persistente + Firebase auth global.
 *   HeaderSection vive en AppShell (fuera de <Routes>) — nunca se desmonta.
 *   AuthModal, AdminPanel y EditorRequestForm también están en AppShell para
 *   que funcionen desde CUALQUIER ruta (FAQ, Hoja de ruta, etc.).
 *
 * Usa HashRouter para compatibilidad con GitHub Pages
 */

import { useState, useCallback, useLayoutEffect, useEffect, lazy, Suspense } from 'react'
import { HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AppProvider, useAppContext } from './contexts/AppContext'
import { useAuth } from './contexts/AuthContext'
import { HeaderSection } from './components/home/sections/HeaderSection'
import { useConversor } from './hooks/useConversor'
import { useVaciadoAlPerderSesion } from './hooks/useVaciadoAlPerderSesion'
import { useRevisionDesdeCorreo, type PanelDeRevision } from './hooks/useRevisionDesdeCorreo'
import { CoordConversorModal } from './components/shared/CoordConversorModal'
import { AuthModal } from './components/auth/AuthModal'
import { AdminPanel } from './components/admin/AdminPanel'
import { ModeratorPanel } from './components/admin/ModeratorPanel'
import { SinPermisoPanel } from './components/admin/SinPermisoPanel'
import { EditorRequestForm } from './components/auth/EditorRequestForm'
import { HomePage } from './components/home'
import OfflineIndicator from './components/OfflineIndicator'

// ── Rutas con lazy loading (14 chunks separados del bundle inicial) ──────────
const WizardPageV2         = lazy(() => import('./components/WizardPageV2'))
const FaqPage              = lazy(() => import('./components/home/FaqPage').then(m => ({ default: m.FaqPage })))
const Prospectiva          = lazy(() => import('./pages/Prospectiva').then(m => ({ default: m.Prospectiva })))
const ExportPage           = lazy(() => import('./pages/ExportPage').then(m => ({ default: m.ExportPage })))
const TransparenciaPage    = lazy(() => import('./components/home/TransparenciaPage').then(m => ({ default: m.TransparenciaPage })))
const LegalPage            = lazy(() => import('./components/home/LegalPage').then(m => ({ default: m.LegalPage })))
const AuthActionPage       = lazy(() => import('./pages/AuthActionPage').then(m => ({ default: m.AuthActionPage })))
const GreaSismoPage        = lazy(() => import('./pages/GreaSismoPage').then(m => ({ default: m.GreaSismoPage })))

// ── Rutas de depuración: solo en desarrollo (SEC0-c3) ────────────────────────
// En producción Vite sustituye `import.meta.env.DEV` por `false` y Rollup quita
// la rama entera: ni rutas ni chunks llegan a pteltool.es. `/debug/step2` monta
// el P2 antiguo con «Confirmar coords», que escribe en PostGIS con la sesión real.
export const DEBUG_ROUTES = import.meta.env.DEV
  ? [
      { path: '/debug/step2',             Page: lazy(() => import('./pages/DebugStep2Page').then(m => ({ default: m.DebugStep2Page }))) },
      { path: '/debug/step3',             Page: lazy(() => import('./pages/DebugStep3Page').then(m => ({ default: m.DebugStep3Page }))) },
      { path: '/test/hover-alternatives', Page: lazy(() => import('./pages/HoverAlternativesDemo').then(m => ({ default: m.HoverAlternativesDemo }))) },
      { path: '/test/map-perf',           Page: lazy(() => import('./pages/MapPerfHarness')) },
      { path: '/test/step1',              Page: lazy(() => import('./pages/TestStep1Page').then(m => ({ default: m.TestStep1Page }))) },
    ]
  : []

/** Fallback minimalista mientras carga un chunk de ruta */
function RouteLoader() {
  return (
    <div
      role="status"
      aria-label="Cargando página"
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}
    >
      <div style={{ width: 32, height: 32, border: '3px solid #a3e635', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function AppShell() {
  const { theme, toggleTheme, registerLoginModal, notifyAuth } = useAppContext();
  const {
    user, userData, isAdmin,
    login, register, logout,
    resetPassword, resendVerification,
    error: authError, clearError,
    networkBlocked,
    loading: authLoading,
  } = useAuth();

  const location = useLocation();

  // SALDOC: al perderse la sesión (botón, caducidad u otra pestaña), la pestaña
  // vuelve a la portada sin documento ni datos de la cuenta.
  useVaciadoAlPerderSesion(user);

  // ── Auth modal ──────────────────────────────────────────────────────────────
  const [isAuthModalOpen,   setIsAuthModalOpen]   = useState(false);
  const [authInitialView,   setAuthInitialView]   = useState<'login' | 'register'>('login');
  const [isAuthLoading,   setIsAuthLoading]   = useState(false);
  const [isAdminPanelOpen,    setIsAdminPanelOpen]    = useState(false);
  // SEC0-b2c-2: el moderador tiene su propio panel y su botón en la cabecera.
  const [isModeratorPanelOpen, setIsModeratorPanelOpen] = useState(false);
  const isModerator = userData?.role === 'moderator';
  const [isEditorRequestOpen, setIsEditorRequestOpen] = useState(false);
  const { isOpen: isConversorOpen, open: openConversor, close: closeConversor } = useConversor();

  // ── Scroll to top en cada cambio de ruta ───────────────────────────────────
  useLayoutEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);

  // ── ENLROL: el enlace del correo (?admin=1) abre el panel de su rol ────────
  // DOP-1 (a) con el rol ya cargado; DOP-3 (a) sin sesión se invita a entrar.
  const [isSinPermisoOpen, setIsSinPermisoOpen] = useState(false);
  const abrirEntradaParaRevisar = useCallback(() => {
    setAuthInitialView('login');
    setIsAuthModalOpen(true);
  }, []);
  const abrirPanelDeRevision = useCallback((panel: PanelDeRevision) => {
    if (panel === 'administracion') setIsAdminPanelOpen(true);
    else if (panel === 'moderacion') setIsModeratorPanelOpen(true);
    else setIsSinPermisoOpen(true);
  }, []);
  const { recordarRevision } = useRevisionDesdeCorreo({
    uid: user?.uid ?? null,
    datos: userData,
    cargando: authLoading,
    abrirEntrada: abrirEntradaParaRevisar,
    abrirPanel: abrirPanelDeRevision,
  });

  // ── Registrar abridor en AppContext ────────────────────────────────────────
  useEffect(() => {
    registerLoginModal(() => setIsAuthModalOpen(true));
  }, [registerLoginModal]);

  // ── Sincronizar Firebase → AppContext ──────────────────────────────────────
  // FIX BUG: cuando el usuario ya tiene sesión activa (token persistente entre
  // visitas), AppContext.isAuthenticated arrancaba en false porque solo leía
  // localStorage con validación de fecha. Al escuchar aquí el usuario real de
  // Firebase, la HomePage recibe isAuthenticated=true y muestra la dropzone.
  useEffect(() => {
    const displayName = userData?.displayName ?? user?.email?.split('@')[0] ?? '';
    notifyAuth(!!user, user ? displayName : undefined);
  }, [user, userData, notifyAuth]);

  // ── Handlers Firebase ──────────────────────────────────────────────────────
  const handleLogin = useCallback(async (email: string, password: string) => {
    setIsAuthLoading(true);
    try {
      await login(email, password);
      setIsAuthModalOpen(false);
    } finally {
      setIsAuthLoading(false);
    }
  }, [login]);

  const handleRegister = useCallback(async (
    email: string, password: string, displayName: string,
    firstName: string, lastName: string
  ) => {
    setIsAuthLoading(true);
    try {
      await register(email, password, displayName || `${firstName} ${lastName}`);
    } finally {
      setIsAuthLoading(false);
    }
  }, [register]);

  const handlePasswordReset = useCallback(async (email: string) => {
    setIsAuthLoading(true);
    try { await resetPassword(email); }
    finally { setIsAuthLoading(false); }
  }, [resetPassword]);

  const handleResendVerification = useCallback(async (email: string, password: string) => {
    setIsAuthLoading(true);
    try { await resendVerification(email, password); }
    finally { setIsAuthLoading(false); }
  }, [resendVerification]);

  const handleLogout = useCallback(async () => { await logout(); }, [logout]);

  // ENLROL DOP-2/3 (a): «Cambiar de cuenta» cierra la sesión y deja armada la
  // revisión, para que la cuenta con la que se entre abra su panel.
  const handleCambiarCuenta = useCallback(async () => {
    setIsSinPermisoOpen(false);
    recordarRevision();
    await logout();
  }, [logout, recordarRevision]);

  const handleClose = useCallback(() => {
    setIsAuthModalOpen(false);
    clearError();
  }, [clearError]);

  const userDisplayName = userData?.displayName ?? user?.email?.split('@')[0] ?? '';

  return (
    <>
      {/* Banner de red bloqueada — aparece cuando Firebase Auth no responde */}
      {networkBlocked && (
        <div role="alert" style={{
          position: 'sticky', top: 0, zIndex: 9999,
          background: '#854F0B', color: '#FAEEDA',
          padding: '10px 20px', fontSize: '13px',
          display: 'flex', alignItems: 'center', gap: '10px',
          fontFamily: 'var(--font-sans, system-ui)',
        }}>
          <span style={{ fontSize: '16px' }}>⚠</span>
          <span>
            <strong>Conexión limitada:</strong> No se puede contactar con los servidores de autenticación.
            Puede que un bloqueador de anuncios o el firewall de la red esté filtrando el tráfico.
            Desactiva el bloqueador para <strong>pteltool.es</strong> y recarga la página.
          </span>
        </div>
      )}
      {/* Header persistente — nunca se desmonta */}
      <HeaderSection
        theme={theme}
        onToggleTheme={toggleTheme}
        isAuthenticated={!!user}
        username={userDisplayName}
        onLoginClick={(mode) => {
          setAuthInitialView(mode ?? 'login');
          setIsAuthModalOpen(true);
        }}
        onLogout={handleLogout}
        isAdmin={isAdmin}
        onOpenAdmin={() => setIsAdminPanelOpen(true)}
        isModerator={isModerator}
        onOpenModerator={() => setIsModeratorPanelOpen(true)}
        onRequestEditor={() => setIsEditorRequestOpen(true)}
        onOpenConversor={openConversor}
      />

      <Suspense fallback={<RouteLoader />}>
        <Routes>
          <Route path="/" element={<HomePage onOpenConversor={openConversor} />} />
          <Route path="/wizard" element={<WizardPageV2 />} />
          <Route path="/wizard/:step" element={<WizardPageV2 />} />
          <Route path="/export" element={<ExportPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/roadmap" element={<Prospectiva />} />
          <Route path="/transparencia" element={<TransparenciaPage />} />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/opciones" element={<Navigate to="/roadmap" replace />} />
          <Route path="/prospectiva" element={<Navigate to="/roadmap" replace />} />
          {/* Rutas de depuración: vacías en producción (DEBUG_ROUTES) */}
          {DEBUG_ROUTES.map(({ path, Page }) => (
            <Route key={path} path={path} element={<Page />} />
          ))}
          <Route path="/auth/action" element={<AuthActionPage />} />
          <Route path="/grea/sismo" element={<GreaSismoPage />} />
        </Routes>
      </Suspense>

      {/* ── Modales globales — disponibles en todas las rutas ── */}
      <AuthModal
        theme={theme}
        initialView={authInitialView}
        isOpen={isAuthModalOpen}
        onClose={handleClose}
        onLogin={handleLogin}
        onRegister={handleRegister}
        onPasswordReset={handlePasswordReset}
        onResendVerification={handleResendVerification}
        isLoading={isAuthLoading}
        error={authError}
        onClearError={clearError}
        onLoginSuccess={handleClose}
      />

      <AdminPanel
        open={isAdminPanelOpen}
        onOpenChange={setIsAdminPanelOpen}
        theme={theme}
      />

      <ModeratorPanel
        open={isModeratorPanelOpen}
        onOpenChange={setIsModeratorPanelOpen}
        theme={theme}
      />

      <SinPermisoPanel
        open={isSinPermisoOpen}
        onOpenChange={setIsSinPermisoOpen}
        theme={theme}
        correo={userData?.email || user?.email || ''}
        rol={userData?.role ?? null}
        onCambiarCuenta={() => { void handleCambiarCuenta(); }}
      />

      {/* EditorRequestForm: disponible; se activa desde AdminPanel */}
      <EditorRequestForm
        open={isEditorRequestOpen}
        onClose={() => setIsEditorRequestOpen(false)}
        theme={theme}
      />

      {/* Indicador offline — visible en todas las rutas (FUT-04) */}
      <OfflineIndicator position="bottom-right" />

      {/* Modal conversor — accesible desde Header, Footer y Menú */}
      <CoordConversorModal isOpen={isConversorOpen} onClose={closeConversor} />
    </>
  );
}

function App() {
  return (
    <AppProvider>
      <HashRouter>
        <AppShell />
      </HashRouter>
    </AppProvider>
  );
}

export default App
